import type { Ref } from 'vue';

// Mecânica compartilhada das "cascas" slide-up da tela de jogo (modal de
// depósito e de notificações): trava o scroll do body enquanto aberto e
// previne o rubber-band do iOS no scroller interno.
//
// O lock do body é o useBodyScrollLock compartilhado (contador), nunca
// `body.style.overflow` na mão: o pull-to-refresh do app só se desliga quando
// o contador está > 0, e o reset manual apagava o lock de qualquer modal ainda
// aberto por baixo (KYC do containment, hub do Clube). Ele também é
// `immediate`, então cobre o sheet que monta já aberto.
//
// Uso: const { scrollerRef } = useSlideUpSheet(isOpen); e amarrar `scrollerRef`
// ao elemento que rola dentro do sheet.
export function useSlideUpSheet(isOpen: Ref<boolean>) {
  const scrollerRef = ref<HTMLElement | null>(null);

  let touchStartY = 0;
  let touchStartX = 0;

  function onTouchStart(e: TouchEvent) {
    touchStartY = e.touches[0]?.clientY ?? 0;
    touchStartX = e.touches[0]?.clientX ?? 0;
  }

  function onTouchMove(e: TouchEvent) {
    const el = scrollerRef.value;

    if (!el) return;

    const currentY = e.touches[0]?.clientY ?? 0;
    const currentX = e.touches[0]?.clientX ?? 0;
    const deltaY = currentY - touchStartY;
    const deltaX = currentX - touchStartX;

    // Só interfere em gesto predominantemente vertical.
    if (Math.abs(deltaX) > Math.abs(deltaY)) return;

    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

    // Puxando pra baixo no topo, ou pra cima no fim → cancela pra não esticar.
    if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
      e.preventDefault();
    }
  }

  useBodyScrollLock(isOpen);

  // Liga/desliga o guard anti rubber-band conforme abre/fecha e o scroller monta.
  watch(
    [isOpen, scrollerRef],
    ([open, el]) => {
      if (!el) return;
      if (open) {
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
      } else {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
      }
    },
    { flush: 'post' },
  );

  return { scrollerRef };
}
